import React, { useEffect, useState } from 'react';
import { API_URL, Pipeline } from '../../types';
import { useNavigate, useParams } from 'react-router-dom';
import CodeEditor from '../../components/CodeEditor';
import { useTheme } from '../../ThemeContext';
import { SourceConnectionForm } from '../../components/pipeline/SourceConnectionForm';
import { DestinationConnectionForm } from '../../components/pipeline/DestinationConnectionForm';
import { ConnectionPreview } from '../../components/pipeline/ConnectionPreview';
import { PipelineBasicInfo } from '../../components/pipeline/PipelineBasicInfo';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { detectDriver } from '../../utils/driverDetection';
import { apiGet, apiPost, apiPut } from '../../utils/apiClient';

export default function PipelineEditorPage() {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // Job type - determines if this is a Spark data pipeline or report generation job
    jobType: 'spark' as 'spark' | 'report',
    reportId: '',
    // Source connector type
    sourceType: 'jdbc' as 'jdbc' | 'excel_directory' | 'csv_directory' | 'parquet_directory' | 'azure_blob_storage',

    // JDBC Source fields
    sourceUrl: isEditMode ? '' : 'jdbc:mysql://mysql-source:3306/sales_db',
    sourceDriver: isEditMode ? '' : 'com.mysql.cj.jdbc.Driver',
    sourceDriverUrl: '',
    sourceTable: isEditMode ? '' : 'sales_data',
    sourceUser: isEditMode ? '' : 'sales_user',
    sourcePassword: isEditMode ? '' : 'sales_pass',

    // Excel directory source options
    excelPath: '/app/data/source/excel',
    excelPattern: '*.xlsx',
    excelHeader: true,
    excelInferSchema: true,
    excelSheet: 'Sheet1',
    excelDataAddress: '',
    excelRecursive: false,
    excelSkipTransformations: false,
    excelPureCopy: false,

    // CSV directory source options
    csvPath: '/app/data/source/csv',
    csvPattern: '*.csv',
    csvHeader: true,
    csvInferSchema: true,

    // Parquet directory source options
    parquetPath: '/app/data/source/parquet',
    parquetPattern: '',
    parquetMergeSchema: false,

    // Azure Blob Storage source options
    azureStorageAccountName: '',
    azureStorageAccountKey: '',
    azureContainer: '',
    azurePath: '',
    azureFormat: 'parquet',
    azureHeader: true,
    azureInferSchema: true,

    // Target connector type
    targetType: 'jdbc' as 'jdbc' | 'excel_directory' | 'csv_directory' | 'parquet_directory' | 'azure_blob',

    // Target JDBC fields
    targetUrl: isEditMode ? '' : 'jdbc:postgresql://postgres-target:5432/data_warehouse',
    targetDriver: isEditMode ? '' : 'org.postgresql.Driver',
    targetDriverUrl: '',
    targetTable: isEditMode ? '' : 'processed_sales',
    targetUser: isEditMode ? '' : 'warehouse_user',
    targetPassword: isEditMode ? '' : 'warehouse_pass',

    // Target CSV options
    targetCsvPath: '',
    targetCsvHeader: true,
    targetCsvDelimiter: ',',

    // Target Parquet options
    targetParquetPath: '',
    targetParquetPartitionBy: '',
    targetParquetRePartitionBy: '',

    // Target Excel options
    targetExcelPath: '',
    targetExcelSheet: 'Sheet1',
    targetExcelDataAddress: '',
    targetExcelHeader: true,

    // Target Azure Blob Storage options
    targetAzureBlobStorageAccount: '',
    targetAzureBlobSasToken: '',
    targetAzureBlobContainer: '',
    targetAzureBlobPath: '',
    targetAzureBlobFormat: 'parquet',
    targetAzureBlobCsvDelimiter: ',',
    targetAzureBlobCsvHeader: true,
    targetAzureBlobCompression: 'snappy',
    targetAzureBlobExcelSheet: 'Sheet1',
    targetAzureBlobExcelDataAddress: '',
    targetAzureBlobExcelHeader: true,

    // Common write mode
    mode: 'overwrite',
    transformations: [] as any[],
    artifacts: [] as Array<{ name: string; path: string; description?: string }>,
  });

  const sourceSuggestion = detectDriver(formData.sourceUrl);
  const targetSuggestion = detectDriver(formData.targetUrl);

  // Preview states
  const [srcPrev, setSrcPrev] = useState<{ columns: string[]; rows: any[][] } | null>(null);
  const [tgtPrev, setTgtPrev] = useState<{ columns: string[]; rows: any[][] } | null>(null);
  const [srcPrevLoading, setSrcPrevLoading] = useState(false);
  const [tgtPrevLoading, setTgtPrevLoading] = useState(false);
  const [srcPrevError, setSrcPrevError] = useState<string | null>(null);
  const [tgtPrevError, setTgtPrevError] = useState<string | null>(null);

  // SQL validation state per transformation id
  const [sqlValidation, setSqlValidation] = useState<Record<string, { loading: boolean; valid?: boolean; error?: string }>>({});
  // Schema detection state per transformation id
  const [schemaMap, setSchemaMap] = useState<Record<string, Record<string, any>>>({});
  const [schemaLoading, setSchemaLoading] = useState<Record<string, boolean>>({});
  const [schemaError, setSchemaError] = useState<Record<string, string | undefined>>({});

  // Collapsible section states for better UX when editing transformations
  const [sourceCollapsed, setSourceCollapsed] = useState(false);
  const [destinationCollapsed, setDestinationCollapsed] = useState(false);

  // Available reports for dropdown
  const [availableReports, setAvailableReports] = useState<Array<{ report_id: string; report_name: string }>>([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const previewSource = async () => {
    setSrcPrevLoading(true);
    setSrcPrevError(null);
    setSrcPrev(null);
    try {
      const body = {
        connection: {
          url: formData.sourceUrl,
          driver: formData.sourceDriver,
          driver_url: formData.sourceDriverUrl || undefined,
          table: formData.sourceTable,
          user: formData.sourceUser,
          password: formData.sourcePassword,
        },
        limit: 5,
      };
      const res = await apiPost(`${API_URL}/api/preview`, body);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to preview source');
      setSrcPrev(data);
    } catch (e: any) {
      setSrcPrevError(e?.message || 'Failed to preview source');
    } finally {
      setSrcPrevLoading(false);
    }
  };

  const previewTarget = async () => {
    if (formData.targetType !== 'jdbc') {
      setTgtPrevError('Preview is only available for JDBC targets');
      setTgtPrevLoading(false);
      setTgtPrev(null);
      return;
    }
    setTgtPrevLoading(true);
    setTgtPrevError(null);
    setTgtPrev(null);
    try {
      const body = {
        connection: {
          url: formData.targetUrl,
          driver: formData.targetDriver,
          driver_url: formData.targetDriverUrl || undefined,
          table: formData.targetTable,
          user: formData.targetUser,
          password: formData.targetPassword,
        },
        limit: 5,
      };
      const res = await apiPost(`${API_URL}/api/preview`, body);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to preview target');
      setTgtPrev(data);
    } catch (e: any) {
      setTgtPrevError(e?.message || 'Failed to preview target');
    } finally {
      setTgtPrevLoading(false);
    }
  };

  // --- Transformations editor helpers ---
  const genId = () => 't-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);

  type TransformType = 'filter' | 'select' | 'add_column' | 'rename' | 'drop' | 'cast' | 'normalize' | 'aggregate' | 'join' | 'deduplicate' | 'custom_sql' | 'pyspark';

  const addTransformation = (t: TransformType) => {
    const base: any = { id: genId(), type: t, config: {} };
    switch (t) {
      case 'filter':
        base.config = { condition: '' };
        break;
      case 'select':
        base.config = { columns: [] as string[] };
        break;
      case 'add_column':
        base.config = { name: '', expression: '' };
        break;
      case 'rename':
        base.config = { from: '', to: '' };
        break;
      case 'drop':
        base.config = { columns: [] as string[] };
        break;
      case 'cast':
        base.config = { column: '', type: '' };
        break;
      case 'normalize':
        base.config = { columns: [] as string[], method: '' };
        break;
      case 'aggregate':
        base.config = { group_by: [] as string[], aggregations: [] as Array<{ column: string; func: string; alias?: string }> };
        break;
      case 'join':
        base.config = { how: 'inner', on: [] as Array<{ left: string; right: string }>, with: { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } } };
        break;
      case 'deduplicate':
        base.config = { subset: [] as string[], keep: 'first' };
        break;
      case 'custom_sql':
        base.config = { sql: '' };
        break;
      case 'pyspark':
        base.config = { code: '' };
        break;
    }
    setFormData(prev => ({ ...prev, transformations: [...prev.transformations, base] }));
  };

  const updateTransformation = (id: string, patch: any) => {
    setFormData(prev => ({
      ...prev,
      transformations: prev.transformations.map((t: any) => (t.id === id ? { ...t, ...patch } : t)),
    }));
  };

  const updateTransformationConfig = (id: string, configPatch: any) => {
    setFormData(prev => ({
      ...prev,
      transformations: prev.transformations.map((t: any) => (t.id === id ? { ...t, config: { ...t.config, ...configPatch } } : t)),
    }));
  };

  const removeTransformation = (id: string) => {
    setFormData(prev => ({ ...prev, transformations: prev.transformations.filter((t: any) => t.id !== id) }));
  };

  const moveTransformation = (id: string, dir: -1 | 1) => {
    setFormData(prev => {
      const idx = prev.transformations.findIndex((t: any) => t.id === id);
      if (idx < 0) return prev;
      const next = [...prev.transformations];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[swapIdx];
      next[swapIdx] = tmp;
      return { ...prev, transformations: next };
    });
  };

  const handleSourceUrlChange = (value: string) => {
    const suggestion = detectDriver(value);
    setFormData(prev => ({
      ...prev,
      sourceUrl: value,
      sourceDriver: prev.sourceDriver || suggestion?.driver || '',
      sourceDriverUrl: prev.sourceDriverUrl || suggestion?.driver_url || '',
    }));
  };
  const handleTargetUrlChange = (value: string) => {
    const suggestion = detectDriver(value);
    setFormData(prev => ({
      ...prev,
      targetUrl: value,
      targetDriver: prev.targetDriver || suggestion?.driver || '',
      targetDriverUrl: prev.targetDriverUrl || suggestion?.driver_url || '',
    }));
  };

  const applySourceDetected = () => {
    if (sourceSuggestion) setFormData(prev => ({ ...prev, sourceDriver: sourceSuggestion.driver, sourceDriverUrl: sourceSuggestion.driver_url }));
  };
  const applyTargetDetected = () => {
    if (targetSuggestion) setFormData(prev => ({ ...prev, targetDriver: targetSuggestion.driver, targetDriverUrl: targetSuggestion.driver_url }));
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const config = JSON.parse(text);
      
      if (!config.source || !config.target) {
        throw new Error('Invalid pipeline configuration: missing source or target');
      }

      const newFormData: any = {
        name: config.name || '',
        description: config.description || '',
        transformations: config.transformations || [],
      };

      const source = config.source || {};
      const sourceType = source.type || 'jdbc';
      newFormData.sourceType = sourceType;

      if (sourceType === 'jdbc' && source.connection) {
        newFormData.sourceUrl = source.connection.url || '';
        newFormData.sourceDriver = source.connection.driver || '';
        newFormData.sourceDriverUrl = source.connection.driver_url || '';
        newFormData.sourceTable = source.connection.table || '';
        newFormData.sourceUser = source.connection.user || '';
        newFormData.sourcePassword = source.connection.password || '';
      } else if (sourceType === 'excel_directory' && source.options) {
        newFormData.excelPath = source.options.path || '';
        newFormData.excelPattern = source.options.pattern || '*.xlsx';
        newFormData.excelHeader = source.options.header !== undefined ? source.options.header : true;
        newFormData.excelInferSchema = source.options.inferSchema !== undefined ? source.options.inferSchema : true;
        newFormData.excelSheet = source.options.sheet || 'Sheet1';
        newFormData.excelDataAddress = source.options.dataAddress || '';
        newFormData.excelRecursive = source.options.recursive || false;
        newFormData.excelSkipTransformations = source.options.skipTransformations || false;
        newFormData.excelPureCopy = source.options.pureCopy || false;
      } else if (sourceType === 'csv_directory' && source.options) {
        newFormData.csvPath = source.options.path || '';
        newFormData.csvPattern = source.options.pattern || '*.csv';
        newFormData.csvHeader = source.options.header !== undefined ? source.options.header : true;
        newFormData.csvInferSchema = source.options.inferSchema !== undefined ? source.options.inferSchema : true;
      } else if (sourceType === 'parquet_directory' && source.options) {
        newFormData.parquetPath = source.options.path || '';
        newFormData.parquetPattern = source.options.pattern || '';
        newFormData.parquetMergeSchema = source.options.mergeSchema || false;
      } else if (sourceType === 'azure_blob_storage' && source.options) {
        newFormData.azureStorageAccountName = source.options.storage_account_name || '';
        newFormData.azureStorageAccountKey = source.options.storage_account_key || '';
        newFormData.azureContainer = source.options.container || '';
        newFormData.azurePath = source.options.path || '';
        newFormData.azureFormat = source.options.format || 'parquet';
        newFormData.azureHeader = source.options.header !== undefined ? source.options.header : true;
        newFormData.azureInferSchema = source.options.inferSchema !== undefined ? source.options.inferSchema : true;
      }

      const target = config.target || {};
      const targetType = target.type || 'jdbc';
      newFormData.targetType = targetType;
      
      if (targetType === 'jdbc' && target.connection) {
        newFormData.targetUrl = target.connection.url || '';
        newFormData.targetDriver = target.connection.driver || '';
        newFormData.targetDriverUrl = target.connection.driver_url || '';
        newFormData.targetTable = target.connection.table || '';
        newFormData.targetUser = target.connection.user || '';
        newFormData.targetPassword = target.connection.password || '';
      } else if (targetType === 'csv_directory' && target.options) {
        newFormData.targetCsvPath = target.options.path || '';
        newFormData.targetCsvHeader = target.options.header !== undefined ? target.options.header : true;
        newFormData.targetCsvDelimiter = target.options.delimiter || ',';
      } else if (targetType === 'parquet_directory' && target.options) {
        newFormData.targetParquetPath = target.options.path || '';
        newFormData.targetParquetPartitionBy = target.options.partitionBy || '';
        newFormData.targetParquetRePartitionBy = target.options.rePartitionBy || '';
      } else if (targetType === 'excel_directory' && target.options) {
        newFormData.targetExcelPath = target.options.path || '';
        newFormData.targetExcelSheet = target.options.sheet || 'Sheet1';
        newFormData.targetExcelDataAddress = target.options.dataAddress || '';
        newFormData.targetExcelHeader = target.options.header !== undefined ? target.options.header : true;
      } else if (targetType === 'azure_blob' && target.options) {
        newFormData.targetAzureBlobStorageAccount = target.options.storage_account_name || '';
        newFormData.targetAzureBlobSasToken = target.options.sas_token || '';
        newFormData.targetAzureBlobContainer = target.options.container || '';
        newFormData.targetAzureBlobPath = target.options.path || '';
        newFormData.targetAzureBlobFormat = target.options.format || 'parquet';
        newFormData.targetAzureBlobCsvDelimiter = target.options.delimiter || ',';
        newFormData.targetAzureBlobCsvHeader = target.options.header !== undefined ? target.options.header : true;
        newFormData.targetAzureBlobCompression = target.options.compression || 'snappy';
        newFormData.targetAzureBlobExcelSheet = target.options.sheet || 'Sheet1';
        newFormData.targetAzureBlobExcelDataAddress = target.options.dataAddress || '';
        newFormData.targetAzureBlobExcelHeader = target.options.header !== undefined ? target.options.header : true;
      }

      if (target.mode) {
        newFormData.mode = target.mode;
      }

      setFormData(prev => ({ ...prev, ...newFormData }));
      alert('Pipeline configuration pasted successfully!');
    } catch (e: any) {
      alert(e?.message || 'Failed to paste pipeline configuration. Make sure you have a valid pipeline config in your clipboard.');
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      setLoading(false);
      return;
    }
    
    const load = async () => {
      if (!id) return;
      try {
        const res = await apiGet(`/api/pipelines/${id}`);
        if (!res.ok) throw new Error('Pipeline not found');
        const p: Pipeline = await res.json();
        setCurrentVersion(p.version);

        const src: any = (p as any).source || {};
        const sType: any = src?.type || 'jdbc';
        const conn: any = src?.connection || {};
        const opts: any = src?.options || {};

        const tgt: any = (p as any).target || {};
        const tType: any = tgt?.type || 'jdbc';
        const tConn: any = tgt?.connection || {};
        const tOpts: any = tgt?.options || {};

        const base: any = {
          name: p.name,
          description: p.description || '',
          jobType: (p as any).job_type || 'spark',
          reportId: (p as any).report_id || '',

          // Target mapping
          targetType: tType,
          // JDBC
          targetUrl: tConn.url || '',
          targetDriver: tConn.driver || '',
          targetDriverUrl: tConn.driver_url || '',
          targetTable: tConn.table || '',
          targetUser: tConn.user || '',
          targetPassword: tConn.password || '',
          // CSV options
          targetCsvPath: tOpts.path || '',
          targetCsvHeader: tOpts.header ?? true,
          targetCsvDelimiter: (tOpts.delimiter !== undefined && tOpts.delimiter !== null) ? String(tOpts.delimiter) : ',',
          // Parquet options
          targetParquetPath: tOpts.path || '',
          targetParquetPartitionBy: tOpts.partitionBy || '',
          targetParquetRePartitionBy: tOpts.rePartitionBy || '',
          // Excel options
          targetExcelPath: tOpts.path || '',
          targetExcelSheet: tOpts.sheet || 'Sheet1',
          targetExcelDataAddress: tOpts.dataAddress || '',
          targetExcelHeader: tOpts.header ?? true,
          // Azure Blob Storage options
          targetAzureBlobStorageAccount: tOpts.storage_account_name || '',
          targetAzureBlobSasToken: tOpts.sas_token || '',
          targetAzureBlobContainer: tOpts.container || '',
          targetAzureBlobPath: tOpts.path || '',
          targetAzureBlobFormat: tOpts.format || 'parquet',
          targetAzureBlobCsvDelimiter: tOpts.delimiter || ',',
          targetAzureBlobCsvHeader: tOpts.header ?? true,
          targetAzureBlobCompression: tOpts.compression || 'snappy',
          targetAzureBlobExcelSheet: tOpts.sheet || 'Sheet1',
          targetAzureBlobExcelDataAddress: tOpts.dataAddress || '',
          targetAzureBlobExcelHeader: tOpts.header ?? true,

          mode: tgt?.mode || 'overwrite',
          transformations: Array.isArray(p.transformations) ? p.transformations : [],
          artifacts: Array.isArray((p as any).artifacts) ? (p as any).artifacts : [],
        };

        if (sType === 'jdbc') {
          setFormData({
            ...base,
            sourceType: 'jdbc',
            sourceUrl: conn.url || '',
            sourceDriver: conn.driver || '',
            sourceDriverUrl: conn.driver_url || '',
            sourceTable: conn.table || '',
            sourceUser: conn.user || '',
            sourcePassword: conn.password || '',
            // reset dir fields to defaults
            excelPath: 'C\\ingest\\inbox',
            excelPattern: '*.xlsx',
            excelHeader: true,
            excelInferSchema: true,
            excelSheet: 'Sheet1',
            excelDataAddress: '',
            excelRecursive: false,
            excelSkipTransformations: false,
            csvPath: 'C\\ingest\\csv',
            csvPattern: '*.csv',
            csvHeader: true,
            csvInferSchema: true,
            parquetPath: 'C\\ingest\\parquet',
            parquetPattern: '*.parquet',
            parquetMergeSchema: false,
            azureStorageAccountName: '',
            azureStorageAccountKey: '',
            azureContainer: '',
            azurePath: '',
            azureFormat: 'parquet',
            azureHeader: true,
            azureInferSchema: true,
          });
        } else if (sType === 'excel_directory') {
          setFormData((prev: any) => ({
            ...base,
            sourceType: 'excel_directory',
            sourceUrl: '',
            sourceDriver: '',
            sourceDriverUrl: '',
            sourceTable: '',
            sourceUser: '',
            sourcePassword: '',
            excelPath: opts.path || prev.excelPath,
            excelPattern: opts.pattern || prev.excelPattern,
            excelHeader: opts.header ?? prev.excelHeader,
            excelInferSchema: opts.inferSchema ?? prev.excelInferSchema,
            excelSheet: opts.sheet || prev.excelSheet,
            excelDataAddress: opts.dataAddress || prev.excelDataAddress,
            excelRecursive: opts.recursive ?? prev.excelRecursive,
            excelSkipTransformations: opts.skipTransformations ?? prev.excelSkipTransformations,
            excelPureCopy: opts.pureCopy ?? prev.excelPureCopy,
            csvPath: prev.csvPath,
            csvPattern: prev.csvPattern,
            csvHeader: prev.csvHeader,
            csvInferSchema: prev.csvInferSchema,
            parquetPath: prev.parquetPath,
            parquetPattern: prev.parquetPattern,
            parquetMergeSchema: prev.parquetMergeSchema,
          }));
        } else if (sType === 'csv_directory') {
          setFormData((prev: any) => ({
            ...base,
            sourceType: 'csv_directory',
            sourceUrl: '',
            sourceDriver: '',
            sourceDriverUrl: '',
            sourceTable: '',
            sourceUser: '',
            sourcePassword: '',
            csvPath: opts.path || prev.csvPath,
            csvPattern: opts.pattern || prev.csvPattern,
            csvHeader: opts.header ?? prev.csvHeader,
            csvInferSchema: opts.inferSchema ?? prev.csvInferSchema,
            excelPath: prev.excelPath,
            excelPattern: prev.excelPattern,
            excelHeader: prev.excelHeader,
            excelInferSchema: prev.excelInferSchema,
            excelSheet: prev.excelSheet,
            excelDataAddress: prev.excelDataAddress,
            excelRecursive: prev.excelRecursive,
            excelSkipTransformations: prev.excelSkipTransformations,
            excelPureCopy: prev.excelPureCopy,
            parquetPath: prev.parquetPath,
            parquetPattern: prev.parquetPattern,
            parquetMergeSchema: prev.parquetMergeSchema,
          }));
        } else if (sType === 'parquet_directory') {
          setFormData((prev: any) => ({
            ...base,
            sourceType: 'parquet_directory',
            sourceUrl: '',
            sourceDriver: '',
            sourceDriverUrl: '',
            sourceTable: '',
            sourceUser: '',
            sourcePassword: '',
            parquetPath: opts.path || prev.parquetPath,
            parquetPattern: opts.pattern || prev.parquetPattern,
            parquetMergeSchema: opts.mergeSchema ?? prev.parquetMergeSchema,
            excelPath: prev.excelPath,
            excelPattern: prev.excelPattern,
            excelHeader: prev.excelHeader,
            excelInferSchema: prev.excelInferSchema,
            excelSheet: prev.excelSheet,
            excelDataAddress: prev.excelDataAddress,
            excelRecursive: prev.excelRecursive,
            excelSkipTransformations: prev.excelSkipTransformations,
            excelPureCopy: prev.excelPureCopy,
            csvPath: prev.csvPath,
            csvPattern: prev.csvPattern,
            csvHeader: prev.csvHeader,
            csvInferSchema: prev.csvInferSchema,
          }));
        } else if (sType === 'azure_blob_storage') {
          setFormData((prev: any) => ({
            ...base,
            sourceType: 'azure_blob_storage',
            sourceUrl: '',
            sourceDriver: '',
            sourceDriverUrl: '',
            sourceTable: '',
            sourceUser: '',
            sourcePassword: '',
            azureStorageAccountName: opts.storage_account_name || '',
            azureStorageAccountKey: opts.storage_account_key || '',
            azureContainer: opts.container || '',
            azurePath: opts.path || '',
            azureFormat: opts.format || 'parquet',
            azureHeader: opts.header ?? true,
            azureInferSchema: opts.inferSchema ?? true,
            excelPath: prev.excelPath,
            excelPattern: prev.excelPattern,
            excelHeader: prev.excelHeader,
            excelInferSchema: prev.excelInferSchema,
            excelSheet: prev.excelSheet,
            excelDataAddress: prev.excelDataAddress,
            excelRecursive: prev.excelRecursive,
            excelSkipTransformations: prev.excelSkipTransformations,
            excelPureCopy: prev.excelPureCopy,
            csvPath: prev.csvPath,
            csvPattern: prev.csvPattern,
            csvHeader: prev.csvHeader,
            csvInferSchema: prev.csvInferSchema,
            parquetPath: prev.parquetPath,
            parquetPattern: prev.parquetPattern,
          }));
        } else {
          // Unknown type: just default to showing JSON elsewhere; keep JDBC fields empty
          setFormData({
            ...base,
            sourceType: 'jdbc',
            sourceUrl: '',
            sourceDriver: '',
            sourceDriverUrl: '',
            sourceTable: '',
            sourceUser: '',
            sourcePassword: '',
            excelPath: 'C\\ingest\\inbox',
            excelPattern: '*.xlsx',
            excelHeader: true,
            excelInferSchema: true,
            excelSheet: 'Sheet1',
            excelDataAddress: '',
            excelRecursive: false,
            excelSkipTransformations: false,
            csvPath: 'C\\ingest\\csv',
            csvPattern: '*.csv',
            csvHeader: true,
            csvInferSchema: true,
            parquetPath: 'C\\ingest\\parquet',
            parquetPattern: '*.parquet',
            azureStorageAccountName: '',
            azureStorageAccountKey: '',
            azureContainer: '',
            azurePath: '',
            azureFormat: 'parquet',
            azureHeader: true,
            azureInferSchema: true,
          });
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load pipeline');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Fetch available reports for dropdown
  useEffect(() => {
    const fetchReports = async () => {
      setReportsLoading(true);
      try {
        const res = await apiGet(`${API_URL}/api/reporting/reports`);
        if (res.ok) {
          const data = await res.json();
          setAvailableReports(data.reports || []);
        }
      } catch (e) {
        console.error('Failed to fetch reports:', e);
      } finally {
        setReportsLoading(false);
      }
    };
    fetchReports();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Build source based on selected connector type
    let source: any;
    if (formData.sourceType === 'jdbc') {
      source = {
        type: 'jdbc',
        connection: {
          url: formData.sourceUrl,
          driver: formData.sourceDriver,
          driver_url: formData.sourceDriverUrl || undefined,
          table: formData.sourceTable,
          user: formData.sourceUser,
          password: formData.sourcePassword,
        },
      };
    } else if (formData.sourceType === 'excel_directory') {
      source = {
        type: 'excel_directory',
        options: {
          path: formData.excelPath,
          pattern: formData.excelPattern,
          header: formData.excelHeader,
          inferSchema: formData.excelInferSchema,
          sheet: formData.excelSheet || undefined,
          dataAddress: formData.excelDataAddress || undefined,
          recursive: formData.excelRecursive,
          skipTransformations: formData.excelSkipTransformations,
          pureCopy: formData.excelPureCopy,
        },
      };
    } else if (formData.sourceType === 'csv_directory') {
      source = {
        type: 'csv_directory',
        options: {
          path: formData.csvPath,
          pattern: formData.csvPattern,
          header: formData.csvHeader,
          inferSchema: formData.csvInferSchema,
        },
      };
    } else if (formData.sourceType === 'parquet_directory') {
      source = {
        type: 'parquet_directory',
        options: {
          path: formData.parquetPath,
          pattern: formData.parquetPattern,
          mergeSchema: formData.parquetMergeSchema || false,
        },
      };
    } else if (formData.sourceType === 'azure_blob_storage') {
      source = {
        type: 'azure_blob_storage',
        options: {
          storage_account_name: formData.azureStorageAccountName || undefined,
          storage_account_key: formData.azureStorageAccountKey || undefined,
          container: formData.azureContainer,
          path: formData.azurePath || undefined,
          format: formData.azureFormat,
          header: formData.azureFormat === 'csv' ? formData.azureHeader : undefined,
          inferSchema: formData.azureFormat === 'csv' ? formData.azureInferSchema : undefined,
        },
      };
    }

    let target: any;
    if (formData.targetType === 'jdbc') {
      target = {
        type: 'jdbc',
        connection: {
          url: formData.targetUrl,
          driver: formData.targetDriver,
          driver_url: formData.targetDriverUrl || undefined,
          table: formData.targetTable,
          user: formData.targetUser,
          password: formData.targetPassword,
        },
        mode: formData.mode,
      };
    } else if (formData.targetType === 'csv_directory') {
      target = {
        type: 'csv_directory',
        options: {
          path: formData.targetCsvPath,
          header: formData.targetCsvHeader,
          delimiter: formData.targetCsvDelimiter,
        },
        mode: formData.mode,
      };
    } else if (formData.targetType === 'parquet_directory') {
      target = {
        type: 'parquet_directory',
        options: {
          path: formData.targetParquetPath,
          partitionBy: formData.targetParquetPartitionBy || undefined,
          rePartitionBy: formData.targetParquetRePartitionBy || undefined,
        },
        mode: formData.mode,
      };
    } else if (formData.targetType === 'excel_directory') {
      target = {
        type: 'excel_directory',
        options: {
          path: formData.targetExcelPath,
          sheet: formData.targetExcelSheet || undefined,
          dataAddress: formData.targetExcelDataAddress || undefined,
          header: formData.targetExcelHeader,
        },
        mode: formData.mode,
      };
    } else if (formData.targetType === 'azure_blob') {
      const options: any = {
        storage_account_name: formData.targetAzureBlobStorageAccount || undefined,
        sas_token: formData.targetAzureBlobSasToken || undefined,
        container: formData.targetAzureBlobContainer,
        path: formData.targetAzureBlobPath || undefined,
        format: formData.targetAzureBlobFormat,
      };
      
      // Add format-specific options
      if (formData.targetAzureBlobFormat === 'csv') {
        options.delimiter = formData.targetAzureBlobCsvDelimiter;
        options.header = formData.targetAzureBlobCsvHeader;
      } else if (formData.targetAzureBlobFormat === 'parquet' || formData.targetAzureBlobFormat === 'orc') {
        options.compression = formData.targetAzureBlobCompression || undefined;
      } else if (formData.targetAzureBlobFormat === 'excel') {
        options.sheet = formData.targetAzureBlobExcelSheet || undefined;
        options.dataAddress = formData.targetAzureBlobExcelDataAddress || undefined;
        options.header = formData.targetAzureBlobExcelHeader;
      }
      
      target = {
        type: 'azure_blob',
        options,
        mode: formData.mode,
      };
    }

    const payload: any = {
      name: formData.name,
      description: formData.description,
      job_type: formData.jobType,
      source,
      transformations: formData.transformations,
      target,
      artifacts: formData.artifacts.length > 0 ? formData.artifacts : undefined,
    };

    // Add report_id for report jobs
    if (formData.jobType === 'report') {
      payload.report_id = formData.reportId;
    }

    try {
      if (isEditMode) {
        const res = await apiPut(`/api/pipelines/${id}`, payload);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.detail || 'Failed to update pipeline');
        }
        alert('Pipeline updated');
        navigate('/pipelines');
      } else {
        const res = await apiPost(`/api/pipelines`, payload);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.detail || 'Failed to create pipeline');
        }
        navigate('/pipelines');
      }
    } catch (e: any) {
      alert(e?.message || (isEditMode ? 'Failed to update pipeline' : 'Failed to create pipeline'));
    }
  };

  if (loading) return <div style={{ color: theme.text }}>Loading...</div>;
  if (error) return <div style={{ color: theme.error }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0, color: theme.text }}>
            {isEditMode ? 'Edit Pipeline' : 'Create Pipeline'}
          </h2>
          {isEditMode && currentVersion && (
            <span
              title="Version being edited"
              style={{
                background: theme.activeBackground,
                color: theme.activeText,
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700
              }}
            >
              Editing v{currentVersion}
            </span>
          )}
        </div>
      </div>
      <p style={{ color: theme.textSecondary, marginTop: 6, marginBottom: 16 }}>
        {isEditMode ? 'Configure source, target and transformations' : 'Define your source and target connections'}
      </p>

      <form onSubmit={handleSubmit}>
        <PipelineBasicInfo
          name={formData.name}
          description={formData.description}
          onNameChange={(value) => setFormData({ ...formData, name: value })}
          onDescriptionChange={(value) => setFormData({ ...formData, description: value })}
          onPaste={!isEditMode ? handlePaste : undefined}
          showPaste={!isEditMode}
        />

        {/* Job Type Selector */}
        <div style={{ marginBottom: 16, padding: 16, border: `1px solid ${theme.inputBorder}`, borderRadius: 8, background: theme.cardBackground }}>
          <h3 style={{ margin: 0, marginBottom: 12, color: theme.text }}>Pipeline Job Type</h3>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, cursor: 'pointer' }}>
              <input
                type="radio"
                name="jobType"
                value="spark"
                checked={formData.jobType === 'spark'}
                onChange={(e) => setFormData({ ...formData, jobType: 'spark' })}
              />
              <span>Spark Data Pipeline</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text, cursor: 'pointer' }}>
              <input
                type="radio"
                name="jobType"
                value="report"
                checked={formData.jobType === 'report'}
                onChange={(e) => setFormData({ ...formData, jobType: 'report' })}
              />
              <span>Report Generation</span>
            </label>
          </div>
          <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 8, marginBottom: 0 }}>
            {formData.jobType === 'spark' 
              ? 'Data transformation pipeline using Spark. Configure source, transformations, and target.'
              : 'Generate reports from existing report templates. No Spark initialization required.'}
          </p>
        </div>

        {/* Report ID Selector for Report Jobs */}
        {formData.jobType === 'report' && (
          <div style={{ marginBottom: 16, padding: 16, border: `1px solid ${theme.inputBorder}`, borderRadius: 8, background: theme.cardBackground }}>
            <h3 style={{ margin: 0, marginBottom: 12, color: theme.text }}>Report Configuration</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, color: theme.text }}>
                Report <span style={{ color: theme.error }}>*</span>
              </label>
              <select
                value={formData.reportId}
                onChange={(e) => setFormData({ ...formData, reportId: e.target.value })}
                required={formData.jobType === 'report'}
                disabled={reportsLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 4,
                  background: theme.inputBackground,
                  color: theme.text,
                  fontSize: 14,
                  cursor: reportsLoading ? 'wait' : 'pointer',
                }}
              >
                <option value="">
                  {reportsLoading ? 'Loading reports...' : 'Select a report'}
                </option>
                {availableReports.map((report) => (
                  <option key={report.report_id} value={report.report_id}>
                    {report.report_name} ({report.report_id})
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4, marginBottom: 0 }}>
                Select an existing report from the list. The report's datasource and configuration will be used.
              </p>
            </div>
          </div>
        )}

        {/* Only show source/target/transformations for Spark jobs */}
        {formData.jobType === 'spark' && isEditMode ? (
          <div style={{ marginBottom: 16 }}>
            <div
              onClick={() => setSourceCollapsed(!sourceCollapsed)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '10px 12px',
                background: theme.hoverBackground,
                borderRadius: 8,
                border: `1px solid ${theme.inputBorder}`,
              }}
            >
              <h3 style={{ margin: 0, color: theme.textSecondary }}>Source Configuration</h3>
              {sourceCollapsed ? <ChevronDown size={20} color={theme.textSecondary} /> : <ChevronUp size={20} color={theme.textSecondary} />}
            </div>

            {!sourceCollapsed && (
              <div style={{ marginTop: 12 }}>
                <SourceConnectionForm
                  sourceType={formData.sourceType}
                  formData={formData}
                  onFormDataChange={(updates) => setFormData({ ...formData, ...updates })}
                  onUrlChange={handleSourceUrlChange}
                  required={false}
                />

                <ConnectionPreview
                  data={srcPrev}
                  loading={srcPrevLoading}
                  error={srcPrevError}
                  onPreview={previewSource}
                  disabled={formData.sourceType !== 'jdbc'}
                  disabledMessage="Source preview is available for JDBC only."
                  label="Preview Source"
                />
              </div>
            )}
          </div>
        ) : formData.jobType === 'spark' ? (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 10, color: theme.text }}>Source Configuration</h3>
            <SourceConnectionForm
              sourceType={formData.sourceType}
              formData={formData}
              onFormDataChange={(updates) => setFormData({ ...formData, ...updates })}
              onUrlChange={handleSourceUrlChange}
              required={true}
            />

            <ConnectionPreview
              data={srcPrev}
              loading={srcPrevLoading}
              error={srcPrevError}
              onPreview={previewSource}
              disabled={formData.sourceType !== 'jdbc'}
              disabledMessage="Source preview is currently supported for JDBC sources only."
              label="Preview Source"
            />
          </div>
        ) : null}

        {formData.jobType === 'spark' && isEditMode ? (
          <div style={{ marginBottom: 16 }}>
            <div
              onClick={() => setDestinationCollapsed(!destinationCollapsed)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '10px 12px',
                background: theme.hoverBackground,
                borderRadius: 8,
                border: `1px solid ${theme.inputBorder}`,
              }}
            >
              <h3 style={{ margin: 0, color: theme.textSecondary }}>Target Configuration</h3>
              {destinationCollapsed ? <ChevronDown size={20} color={theme.textSecondary} /> : <ChevronUp size={20} color={theme.textSecondary} />}
            </div>

            {!destinationCollapsed && (
              <div style={{ marginTop: 12 }}>
                <DestinationConnectionForm
                  targetType={formData.targetType}
                  formData={formData}
                  onFormDataChange={(updates) => setFormData({ ...formData, ...updates })}
                  onUrlChange={handleTargetUrlChange}
                  required={false}
                />

                <ConnectionPreview
                  data={tgtPrev}
                  loading={tgtPrevLoading}
                  error={tgtPrevError}
                  onPreview={previewTarget}
                  disabled={formData.targetType !== 'jdbc'}
                  disabledMessage="Target preview is available for JDBC only."
                  label="Preview Target"
                />
              </div>
            )}
          </div>
        ) : formData.jobType === 'spark' ? (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 10, marginTop: 16, color: theme.text }}>Target Configuration</h3>
            <DestinationConnectionForm
              targetType={formData.targetType}
              formData={formData}
              onFormDataChange={(updates) => setFormData({ ...formData, ...updates })}
              onUrlChange={handleTargetUrlChange}
              required={true}
            />

            <ConnectionPreview
              data={tgtPrev}
              loading={tgtPrevLoading}
              error={tgtPrevError}
              onPreview={previewTarget}
              disabled={formData.targetType !== 'jdbc'}
              disabledMessage="Target preview is currently supported for JDBC targets only."
              label="Preview Target"
            />
          </div>
        ) : null}

        {formData.jobType === 'spark' && (
          <>
            <h3 style={{ color: theme.textSecondary, marginTop: 24 }}>Transformations</h3>
        <div style={{ background: theme.tableHeaderBg, border: `1px solid ${theme.inputBorder}`, borderRadius: 8, padding: 12 }}>
          {formData.transformations.length === 0 && (
            <div style={{ color: theme.textSecondary, marginBottom: 8 }}>No transformations yet. Add one below.</div>
          )}

          {formData.transformations.map((t: any, idx: number) => (
            <div key={t.id || idx} style={{ background: theme.cardBackground, border: `1px solid ${theme.inputBorder}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
                  <span style={{ fontWeight: 700, color: theme.text }}>{idx + 1}.</span>
                  <select
                    value={t.type}
                    onChange={e => updateTransformation(t.id, { type: e.target.value })}
                    style={{ padding: 6, border: `1px solid ${theme.inputBorder}`, borderRadius: 6 }}
                  >
                    <option value="filter">filter</option>
                    <option value="select">select</option>
                    <option value="add_column">add_column</option>
                    <option value="rename">rename</option>
                    <option value="drop">drop</option>
                    <option value="cast">cast</option>
                    <option value="normalize">normalize</option>
                    <option value="aggregate">aggregate</option>
                    <option value="join">join</option>
                    <option value="deduplicate">deduplicate</option>
                    <option value="custom_sql">custom_sql</option>
                    <option value="pyspark">pyspark</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => moveTransformation(t.id, -1)} disabled={idx === 0} style={{ padding: '6px 8px', border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.cardBackground, cursor: idx === 0 ? 'not-allowed' : 'pointer' }}>↑</button>
                  <button type="button" onClick={() => moveTransformation(t.id, 1)} disabled={idx === formData.transformations.length - 1} style={{ padding: '6px 8px', border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.cardBackground, cursor: idx === formData.transformations.length - 1 ? 'not-allowed' : 'pointer' }}>↓</button>
                  <button type="button" onClick={() => removeTransformation(t.id)} style={{ padding: '6px 10px', border: 'none', borderRadius: 6, background: theme.errorLight, color: theme.error, cursor: 'pointer' }}>Remove</button>
                </div>
              </div>

              {/* Dynamic config inputs */}
              {t.type === 'filter' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Condition</label>
                  <input value={t.config?.condition || ''} onChange={e => updateTransformationConfig(t.id, { condition: e.target.value })} placeholder="e.g., amount > 0 AND status = 'PAID'" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                </div>
              )}
              {t.type === 'select' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Columns (comma-separated)</label>
                  <input value={(t.config?.columns || []).join(', ')} onChange={e => updateTransformation(t.id, { config: { columns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })} placeholder="id, name, amount" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                </div>
              )}
              {t.type === 'add_column' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Name</label>
                    <input value={t.config?.name || ''} onChange={e => updateTransformationConfig(t.id, { name: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Expression</label>
                    <input value={t.config?.expression || ''} onChange={e => updateTransformationConfig(t.id, { expression: e.target.value })} placeholder="e.g., amount * 1.2" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                </div>
              )}
              {t.type === 'rename' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>From</label>
                    <input value={t.config?.from || ''} onChange={e => updateTransformationConfig(t.id, { from: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>To</label>
                    <input value={t.config?.to || ''} onChange={e => updateTransformationConfig(t.id, { to: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                </div>
              )}
              {t.type === 'drop' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Columns (comma-separated)</label>
                  <input value={(t.config?.columns || []).join(', ')} onChange={e => updateTransformation(t.id, { config: { columns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })} placeholder="col1, col2" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                </div>
              )}
              {t.type === 'cast' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Column</label>
                    <input value={t.config?.column || ''} onChange={e => updateTransformationConfig(t.id, { column: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Type</label>
                    <input value={t.config?.type || ''} onChange={e => updateTransformationConfig(t.id, { type: e.target.value })} placeholder="e.g., integer, string, date" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                </div>
              )}

              {t.type === 'normalize' && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Columns (comma-separated)</label>
                    <input value={(t.config?.columns || []).join(', ')} onChange={e => updateTransformation(t.id, { config: { ...t.config, columns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })} placeholder="col1, col2" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Method</label>
                    <input value={t.config?.method || ''} onChange={e => updateTransformationConfig(t.id, { method: e.target.value })} placeholder="e.g., zscore, minmax" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                </div>
              )}

              {t.type === 'aggregate' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Group By (comma-separated)</label>
                      <input value={(t.config?.group_by || []).join(', ')} onChange={e => updateTransformation(t.id, { config: { ...t.config, group_by: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })} placeholder="country, segment" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Aggregations (one per line: column,func,alias?)</label>
                      <textarea
                        rows={3}
                        value={Array.isArray(t.config?.aggregations) ? t.config.aggregations.map((a: any) => [a.column, a.func, a.alias].filter(Boolean).join(',')).join('\n') : ''}
                        onChange={e => {
                          const lines = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
                          const aggregations = lines.map(l => {
                            const parts = l.split(',').map(x => x.trim());
                            const [column, func, alias] = [parts[0] || '', parts[1] || '', parts[2]];
                            return alias ? { column, func, alias } : { column, func } as any;
                          });
                          updateTransformation(t.id, { config: { ...t.config, aggregations } });
                        }}
                        placeholder="amount,sum,total_amount\nquantity,avg,avg_qty"
                        style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {t.type === 'join' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Join Type</label>
                    <select value={t.config?.how || 'inner'} onChange={e => updateTransformationConfig(t.id, { how: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}>
                      <option value="inner">inner</option>
                      <option value="left">left</option>
                      <option value="right">right</option>
                      <option value="full">full</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Join Conditions (one per line: left=right)</label>
                    <textarea
                      rows={2}
                      value={Array.isArray(t.config?.on) ? t.config.on.map((p: any) => `${p.left}=${p.right}`).join('\n') : ''}
                      onChange={e => {
                        const pairs = e.target.value.split('\n').map(s => s.trim()).filter(Boolean).map(l => {
                          const [left, right] = l.split('=').map(x => x.trim());
                          return { left, right };
                        });
                        updateTransformation(t.id, { config: { ...t.config, on: pairs } });
                      }}
                      placeholder="a.id=b.id\na.country=b.country"
                      style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}
                    />
                  </div>
                  <div style={{ background: theme.tableHeaderBg, border: `1px solid ${theme.inputBorder}`, borderRadius: 8, padding: 10 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: theme.textSecondary }}>Join Source (JDBC)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>URL</label>
                        <input value={t.config?.with?.connection?.url || ''} onChange={e => {
                          const w = t.config?.with || { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } };
                          updateTransformation(t.id, { config: { ...t.config, with: { ...w, connection: { ...w.connection, url: e.target.value } } } });
                        }} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Driver</label>
                        <input value={t.config?.with?.connection?.driver || ''} onChange={e => {
                          const w = t.config?.with || { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } };
                          updateTransformation(t.id, { config: { ...t.config, with: { ...w, connection: { ...w.connection, driver: e.target.value } } } });
                        }} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Table</label>
                        <input value={t.config?.with?.connection?.table || ''} onChange={e => {
                          const w = t.config?.with || { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } };
                          updateTransformation(t.id, { config: { ...t.config, with: { ...w, connection: { ...w.connection, table: e.target.value } } } });
                        }} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>User</label>
                        <input value={t.config?.with?.connection?.user || ''} onChange={e => {
                          const w = t.config?.with || { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } };
                          updateTransformation(t.id, { config: { ...t.config, with: { ...w, connection: { ...w.connection, user: e.target.value } } } });
                        }} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Password</label>
                        <input type="password" value={t.config?.with?.connection?.password || ''} onChange={e => {
                          const w = t.config?.with || { type: 'jdbc', connection: { url: '', driver: '', table: '', user: '', password: '' } };
                          updateTransformation(t.id, { config: { ...t.config, with: { ...w, connection: { ...w.connection, password: e.target.value } } } });
                        }} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {t.type === 'deduplicate' && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Subset Columns (comma-separated)</label>
                    <input value={(t.config?.subset || []).join(', ')} onChange={e => updateTransformation(t.id, { config: { ...t.config, subset: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } })} placeholder="id, date" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>Keep</label>
                    <select value={t.config?.keep || 'first'} onChange={e => updateTransformationConfig(t.id, { keep: e.target.value })} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}>
                      <option value="first">first</option>
                      <option value="last">last</option>
                    </select>
                  </div>
                </div>
              )}

              {t.type === 'custom_sql' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>SQL</label>
                  <CodeEditor
                    value={t.config?.sql || ''}
                    onChange={(val) => updateTransformationConfig(t.id, { sql: val })}
                    language="sql"
                    theme="light"
                    height="500px"
                    placeholder="SELECT * FROM temp_table WHERE ..."
                    schema={schemaMap[t.id]}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <button type="button" onClick={async () => {
                      const step = formData.transformations.find((x: any) => x.id === t.id);
                      const sql = step?.config?.sql || '';
                      setSqlValidation(prev => ({ ...prev, [t.id]: { loading: true } }));
                      try {
                        const res = await apiPost(`${API_URL}/api/sql/validate`, { sql, connection: { url: formData.sourceUrl, driver: formData.sourceDriver, driver_url: formData.sourceDriverUrl || undefined, table: formData.sourceTable, user: formData.sourceUser, password: formData.sourcePassword } });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data?.detail || 'Validation failed');
                        setSqlValidation(prev => ({ ...prev, [t.id]: { loading: false, valid: !!data?.valid, error: data?.valid ? undefined : (data?.error || 'Invalid SQL') } }));
                      } catch (e: any) {
                        setSqlValidation(prev => ({ ...prev, [t.id]: { loading: false, valid: false, error: e?.message || 'Validation failed' } }));
                      }
                    }} style={{ padding: '6px 10px', border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.cardBackground, cursor: 'pointer' }}>Validate SQL</button>
                    <button type="button" onClick={async () => {
                      setSchemaError(prev => ({ ...prev, [t.id]: undefined }));
                      setSchemaLoading(prev => ({ ...prev, [t.id]: true }));
                      try {
                        let body: any;
                        if (formData.sourceType === 'jdbc') {
                          body = {
                            connection: {
                              url: formData.sourceUrl,
                              driver: formData.sourceDriver,
                              driver_url: formData.sourceDriverUrl || undefined,
                              table: formData.sourceTable,
                              user: formData.sourceUser,
                              password: formData.sourcePassword,
                            }
                          };
                        } else if (formData.sourceType === 'csv_directory') {
                          body = {
                            source: {
                              type: 'csv_directory',
                              options: {
                                path: formData.csvPath,
                                pattern: formData.csvPattern,
                                header: formData.csvHeader,
                                inferSchema: formData.csvInferSchema,
                              }
                            }
                          };
                        } else if (formData.sourceType === 'parquet_directory') {
                          body = {
                            source: {
                              type: 'parquet_directory',
                              options: {
                                path: formData.parquetPath,
                                pattern: formData.parquetPattern,
                              }
                            }
                          };
                        } else if (formData.sourceType === 'excel_directory') {
                          body = {
                            source: {
                              type: 'excel_directory',
                              options: {
                                path: formData.excelPath,
                                pattern: formData.excelPattern,
                                header: formData.excelHeader,
                                inferSchema: formData.excelInferSchema,
                                sheet: formData.excelSheet || undefined,
                                dataAddress: formData.excelDataAddress || undefined,
                                recursive: formData.excelRecursive || undefined,
                              }
                            }
                          };
                        }
                        const res = await apiPost(`${API_URL}/api/schema`, body);
                        const data = await res.json();
                        if (!res.ok) throw new Error(data?.detail || 'Failed to detect schema');
                        const cols: string[] = Array.isArray(data?.columns) ? data.columns.map((c: any) => c.name || String(c)) : [];
                        setSchemaMap(prev => ({ ...prev, [t.id]: { temp_table: cols } }));
                      } catch (e: any) {
                        setSchemaError(prev => ({ ...prev, [t.id]: e?.message || 'Failed to detect schema' }));
                      } finally {
                        setSchemaLoading(prev => ({ ...prev, [t.id]: false }));
                      }
                    }} style={{ padding: '6px 10px', border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.cardBackground, cursor: 'pointer' }}>
                      {schemaLoading[t.id] ? 'Detecting…' : 'Detect schema'}
                    </button>
                    <span style={{ fontSize: 12, color: theme.textSecondary }}>Note: During validation and execution, a temp view named <code style={{ background: theme.codeBackground, padding: '2px 4px', borderRadius: 4 }}>temp_table</code> is available.</span>
                  </div>
                  {sqlValidation[t.id]?.loading && (
                    <div style={{ marginTop: 6, color: theme.textSecondary }}>Validating…</div>
                  )}
                  {schemaError[t.id] && (
                    <div style={{ marginTop: 6, background: theme.errorLight, border: `1px solid ${theme.error}`, color: theme.error, padding: 8, borderRadius: 6 }}>Schema error: {schemaError[t.id]}</div>
                  )}
                  {schemaMap[t.id] && !schemaLoading[t.id] && !schemaError[t.id] && (
                    <div style={{ marginTop: 6, background: theme.successLight, border: `1px solid ${theme.success}`, color: theme.successDark, padding: 8, borderRadius: 6 }}>
                      Detected columns: {(schemaMap[t.id]?.temp_table || []).join(', ') || 'none'}
                    </div>
                  )}
                  {sqlValidation[t.id] && !sqlValidation[t.id].loading && sqlValidation[t.id].valid && (
                    <div style={{ marginTop: 6, background: theme.successLight, border: `1px solid ${theme.success}`, color: theme.successDark, padding: 8, borderRadius: 6 }}>SQL looks valid.</div>
                  )}
                  {sqlValidation[t.id] && !sqlValidation[t.id].loading && sqlValidation[t.id].valid === false && (
                    <div style={{ marginTop: 6, background: theme.errorLight, border: `1px solid ${theme.error}`, color: theme.error, padding: 8, borderRadius: 6 }}>Error: {sqlValidation[t.id]?.error || 'Invalid SQL'}</div>
                  )}
                </div>
              )}

              {t.type === 'pyspark' && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>PySpark Code</label>
                  <CodeEditor
                    value={t.config?.code || ''}
                    onChange={(val) => updateTransformationConfig(t.id, { code: val })}
                    language="text"
                    theme="light"
                    height="500px"
                    placeholder="# Write PySpark code here. The DataFrame is available as 'df'&#10;# Example:&#10;# df = df.filter(df.age > 18)&#10;# df = df.withColumn('new_col', df.existing_col * 2)"
                    schema={schemaMap[t.id]}
                  />
                  <div style={{ marginTop: 8 }}>
                    <span style={{ fontSize: 12, color: theme.textSecondary }}>Note: The input DataFrame is available as <code style={{ background: theme.codeBackground, padding: '2px 4px', borderRadius: 4 }}>df</code>. Make sure to reassign the transformed result back to <code style={{ background: theme.codeBackground, padding: '2px 4px', borderRadius: 4 }}>df</code>.</span>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ color: theme.textSecondary, fontWeight: 600 }}>Add step:</span>
            <select id="add-t-type" style={{ padding: 6, border: `1px solid ${theme.inputBorder}`, borderRadius: 6 }}>
              <option value="filter">filter</option>
              <option value="select">select</option>
              <option value="add_column">add_column</option>
              <option value="rename">rename</option>
              <option value="drop">drop</option>
              <option value="cast">cast</option>
              <option value="normalize">normalize</option>
              <option value="aggregate">aggregate</option>
              <option value="join">join</option>
              <option value="deduplicate">deduplicate</option>
              <option value="custom_sql">custom_sql</option>
              <option value="pyspark">pyspark</option>
            </select>
            <button type="button" onClick={() => {
              const sel = (document.getElementById('add-t-type') as HTMLSelectElement | null)?.value as TransformType | undefined;
              if (sel) addTransformation(sel);
            }} style={{ padding: '6px 12px', border: 'none', borderRadius: 6, background: theme.successLight, color: theme.successDark, cursor: 'pointer' }}>Add</button>
          </div>
        </div>
        </>
        )}

        {/* Artifacts Section */}
        <div style={{ marginTop: 24, padding: 16, background: theme.cardBackground, border: `1px solid ${theme.border}`, borderRadius: 12 }}>
          <h3 style={{ margin: '0 0 12px 0', color: theme.text }}>Artifacts</h3>
          <p style={{ color: theme.textSecondary, fontSize: 14, marginBottom: 16 }}>
            Define artifacts (files or directories) that will be available for download after job execution. 
            If path is blank, all files from /target folder will be copied. Single files are renamed to &#123;name&#125;.&#123;ext&#125;, multiple files are zipped as &#123;name&#125;.zip.
          </p>

          {formData.artifacts.map((artifact, index) => (
            <div key={index} style={{ marginBottom: 12, padding: 12, background: theme.background, border: `1px solid ${theme.border}`, borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: theme.text, fontSize: 13 }}>Name *</label>
                    <input
                      type="text"
                      value={artifact.name}
                      onChange={(e) => {
                        const newArtifacts = [...formData.artifacts];
                        newArtifacts[index] = { ...artifact, name: e.target.value };
                        setFormData({ ...formData, artifacts: newArtifacts });
                      }}
                      placeholder="e.g., Output Report"
                      style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.background, color: theme.text }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: theme.text, fontSize: 13 }}>Path (optional)</label>
                    <input
                      type="text"
                      value={artifact.path}
                      onChange={(e) => {
                        const newArtifacts = [...formData.artifacts];
                        newArtifacts[index] = { ...artifact, path: e.target.value };
                        setFormData({ ...formData, artifacts: newArtifacts });
                      }}
                      placeholder="e.g., output/report.xlsx or leave blank for /target"
                      style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.background, color: theme.text }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newArtifacts = formData.artifacts.filter((_, i) => i !== index);
                    setFormData({ ...formData, artifacts: newArtifacts });
                  }}
                  style={{ 
                    marginLeft: 8, 
                    padding: '8px 12px', 
                    border: `1px solid ${theme.error}`, 
                    background: theme.errorLight, 
                    color: theme.error, 
                    borderRadius: 6, 
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                  title="Remove artifact"
                >
                  Remove
                </button>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: theme.text, fontSize: 13 }}>Description</label>
                <input
                  type="text"
                  value={artifact.description || ''}
                  onChange={(e) => {
                    const newArtifacts = [...formData.artifacts];
                    newArtifacts[index] = { ...artifact, description: e.target.value };
                    setFormData({ ...formData, artifacts: newArtifacts });
                  }}
                  placeholder="Optional description of what this artifact contains"
                  style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.background, color: theme.text }}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => {
              setFormData({
                ...formData,
                artifacts: [...formData.artifacts, { name: '', path: '', description: '' }]
              });
            }}
            style={{ 
              padding: '8px 16px', 
              border: `1px solid ${theme.border}`, 
              background: theme.buttonBackground, 
              color: theme.buttonText, 
              borderRadius: 6, 
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            + Add Artifact
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" onClick={() => navigate('/pipelines')} style={{ padding: '10px 18px', border: `1px solid ${theme.inputBorder}`, background: theme.cardBackground, borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
          <button type="submit" style={{ padding: '10px 18px', border: 'none', background: theme.secondaryButton, color: theme.buttonText, borderRadius: 8, cursor: 'pointer' }}>Save Changes</button>
        </div>
      </form>
    </div>
  );
}




